/**
 * Header component for PowerEditor - contains project name, status, controls, and viewport settings
 */

import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Cloud,
  Settings,
  ChevronLeft,
  Eye,
  EyeOff,
  ChevronDown,
  Pencil,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PublishDropdown } from "@/components/editor/PublishDropdown";
import { GitHubButton } from "@/components/github";
import { GitLabButton } from "@/components/gitlab";

import PowerEditorViewportControls from "./PowerEditorViewportControls";
import ProjectConnectionsPopover from "./ProjectConnectionsPopover";
import { PowerEditorHeaderProps } from "@/types/power-editor.types";
import { useResponsive } from "@/hooks/useResponsive";

export default function PowerEditorHeader({
  project,
  workspace,
  viewMode,
  isSidebarHidden,
  isResponsiveMode,
  viewportSize,
  isCustomViewport,
  customWidth,
  customHeight,
  currentPath,
  availablePaths,
  showDeviceDropdown,
  showPathDropdown,
  projectId,
  onToggleDeviceDropdown,
  onTogglePathDropdown,
  onPathSelect,
  onViewportChange,
  onCustomViewportChange,
  onResponsiveModeChange,
  onReloadPreview,
  onOpenPreviewInNewWindow,
  onSettingsClick,
  isAgentStreaming,
  onRefreshProject,
  onRenameClick,
  onRemoveConnection,
  mobileActiveTab,
  showMobileTabSwitcher,
  onToggleMobileTabSwitcher,
  onViewModeChange,
  onMobileOpenSettings,
  devContainerId,
  previewIframeRef,
}: PowerEditorHeaderProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobilePowerEditor } = useResponsive();
  const navigate = useNavigate();

  const handleCloudClick = () => {
    if (
      viewMode === "settings" &&
      searchParams.get("section") === "optidev-cloud"
    ) {
      // Toggle back to agent mode
      onSettingsClick("");
    } else {
      // Go to OptiDev Cloud settings
      onSettingsClick("optidev-cloud");
    }
  };

  const handleSettingsClick = () => {
    // On mobile, open the settings sheet instead of changing viewMode
    if (isMobilePowerEditor && onMobileOpenSettings) {
      onMobileOpenSettings();
      return;
    }

    if (viewMode === "settings" && searchParams.get("section") === "info") {
      // Toggle back to agent mode
      onSettingsClick("");
    } else {
      // Go to Project Settings
      onSettingsClick("info");
    }
  };

  const handleBackClick = () => {
    // Check if there are tab parameters in URL (like tab=settings)
    const hasTabParams = searchParams.has("tab");

    if (hasTabParams) {
      // If we have tab params, clear them and go back to agent mode
      // This is equivalent to onClose(() => handleViewModeChange("agent"))
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("tab");
      newParams.delete("section");
      setSearchParams(newParams);
      onSettingsClick(""); // This will set viewMode back to "agent"
    } else {
      // No tab params, navigate back to editor list
      navigate("/dashboard/editor");
    }
  };

  return (
    <div className="h-14 border-b flex items-center bg-card border-border overflow-visible">
      {/* Mobile PowerEditor Header */}
      {isMobilePowerEditor && (
        <div className="flex items-center justify-between w-full px-4">
          {/* Left: Back button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Center: Project title dropdown with status */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 outline-none min-w-0">
                {workspace && (
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full flex-shrink-0",
                      workspace.status === "running" && "bg-green-500",
                      workspace.status === "stopped" && "bg-gray-400",
                      (workspace.status === "provisioning" ||
                        workspace.status === "starting") &&
                        "bg-yellow-500 animate-pulse",
                      workspace.status === "stopping" && "bg-orange-500",
                      workspace.status === "error" && "bg-red-500",
                    )}
                  />
                )}
                <span className="text-base font-medium truncate max-w-[140px]">
                  {project?.name || "Untitled Project"}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              sideOffset={8}
              className="w-52 rounded-xl border border-border bg-popover p-1.5 shadow-lg"
            >
              {onRenameClick && (
                <DropdownMenuItem
                  onClick={onRenameClick}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm cursor-pointer"
                >
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                  Rename
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleSettingsClick}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm cursor-pointer"
              >
                <Settings className="h-4 w-4 text-muted-foreground" />
                Project Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                onClick={() => {
                  if (onViewModeChange) {
                    onViewModeChange("history");
                  }
                }}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm cursor-pointer"
              >
                <Clock className="h-4 w-4 text-muted-foreground" />
                Version History
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-1">
            {/* Toggle button - only show on preview tab */}
            {mobileActiveTab === "preview" && (
              <Hint
                text={
                  showMobileTabSwitcher
                    ? "Hide tab switcher"
                    : "Show tab switcher"
                }
                side="bottom"
                align="center"
                delayDuration={0}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleMobileTabSwitcher}
                  className="h-10 w-10 p-0"
                  aria-label={
                    showMobileTabSwitcher
                      ? "Hide tab switcher"
                      : "Show tab switcher"
                  }
                >
                  {showMobileTabSwitcher ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </Hint>
            )}

            {/* Publish button - uses default trigger to show publishing states */}
            <PublishDropdown
              projectId={projectId}
              onNavigateToSettings={onSettingsClick}
              compact
              onMobileOpenSettings={onMobileOpenSettings}
            />
          </div>
        </div>
      )}

      {/* Desktop PowerEditor Header */}
      {!isMobilePowerEditor && (
        <div
          className={cn(
            "flex items-center justify-between min-w-0 pl-3 pr-2",
            !isSidebarHidden && "w-[29%]",
            isSidebarHidden && "max-w-[250px]",
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-sm font-medium outline-none min-w-0">
                  {workspace && (
                    <Hint
                      text={
                        workspace.status.charAt(0).toUpperCase() +
                        workspace.status.slice(1)
                      }
                      side="bottom"
                      align="center"
                      delayDuration={100}
                    >
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full flex-shrink-0",
                          workspace.status === "running" && "bg-green-500",
                          workspace.status === "stopped" && "bg-gray-400",
                          (workspace.status === "provisioning" ||
                            workspace.status === "starting") &&
                            "bg-yellow-500 animate-pulse",
                          workspace.status === "stopping" && "bg-orange-500",
                          workspace.status === "error" && "bg-red-500",
                        )}
                      />
                    </Hint>
                  )}
                  <span className="truncate max-w-[180px]">
                    {project?.name}
                  </span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={8}
                className="w-48 rounded-lg border border-border bg-popover p-1 shadow-lg"
              >
                {onRenameClick && (
                  <DropdownMenuItem
                    onClick={onRenameClick}
                    className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] cursor-pointer"
                  >
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                    Rename
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleSettingsClick}
                  className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] cursor-pointer"
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  Project Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Connections icon - right edge of left section, hidden in settings */}
          {viewMode !== "settings" && (
            <ProjectConnectionsPopover
              projectId={projectId}
              connectionIds={project?.connectionIds ?? []}
              onRemoveConnection={onRemoveConnection}
              isAgentStreaming={
                isAgentStreaming || workspace?.status !== "running"
              }
            />
          )}
        </div>
      )}

      {!isMobilePowerEditor && (
        <>
          {/* Center/Right section: OptiDev Cloud + Viewport controls */}
          <div
            className={cn(
              "flex items-center flex-1 px-3",
              !isSidebarHidden && "w-[71%]",
            )}
          >
            {/* OptiDev Cloud button - aligned with start of preview */}
            {(viewMode === "agent" ||
              viewMode === "visual" ||
              viewMode === "history" ||
              viewMode === "design") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloudClick}
                className="h-8 px-2.5 text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 hover:text-emerald-600 border border-emerald-500/20 gap-1.5 flex-shrink-0"
              >
                <Cloud className="w-3.5 h-3.5" />
                OptiDev Cloud
              </Button>
            )}
            <div className="flex items-center gap-1 flex-1 justify-center">
              {/* Viewport controls - show in agent, visual, history, and design modes */}
              {(viewMode === "agent" ||
                viewMode === "visual" ||
                viewMode === "history" ||
                viewMode === "design") && (
                <PowerEditorViewportControls
                  isResponsiveMode={isResponsiveMode}
                  viewportSize={viewportSize}
                  isCustomViewport={isCustomViewport}
                  customWidth={customWidth}
                  customHeight={customHeight}
                  currentPath={currentPath}
                  availablePaths={availablePaths}
                  showDeviceDropdown={showDeviceDropdown}
                  showPathDropdown={showPathDropdown}
                  showPreview={true} // Always show preview in these modes
                  onResponsiveModeChange={onResponsiveModeChange}
                  onViewportSizeChange={onViewportChange}
                  onCustomViewportChange={onCustomViewportChange}
                  onPathSelect={onPathSelect}
                  onToggleDeviceDropdown={onToggleDeviceDropdown}
                  onTogglePathDropdown={onTogglePathDropdown}
                  onReloadPreview={onReloadPreview}
                  onOpenPreviewInNewWindow={onOpenPreviewInNewWindow}
                  devContainerId={devContainerId}
                  previewIframeRef={previewIframeRef}
                />
              )}
            </div>
          </div>
        </>
      )}
      {!isMobilePowerEditor && (
        <div className="flex items-center gap-2 flex-shrink-0 pr-3">
          {/* Git provider buttons - overlapping stack */}
          {project && (
            <div className="flex items-center -space-x-2">
              <div className="z-[2] relative">
                <GitHubButton
                  projectId={projectId}
                  projectSlug={project.urlSlug}
                  githubConnection={
                    project.githubConnectionId
                      ? {
                          connectionId: project.githubConnectionId,
                          repoUrl: project.githubRepoUrl || "",
                          repoOwner: project.githubRepoOwner || "",
                          repoName: project.githubRepoName || "",
                          defaultBranch: project.githubDefaultBranch || "main",
                          syncEnabled: project.githubSyncEnabled || false,
                        }
                      : undefined
                  }
                  onRefresh={onRefreshProject}
                />
              </div>
              <div className="z-[1] relative">
                <GitLabButton
                  projectId={projectId}
                  projectSlug={project.urlSlug}
                  gitlabConnection={
                    project.gitlabConnectionId
                      ? {
                          connectionId: project.gitlabConnectionId,
                          repoUrl: project.gitlabRepoUrl || "",
                          repoOwner: project.gitlabRepoOwner || "",
                          repoName: project.gitlabRepoName || "",
                          defaultBranch: project.gitlabDefaultBranch || "main",
                          syncEnabled: project.gitlabSyncEnabled || false,
                        }
                      : undefined
                  }
                  onRefresh={onRefreshProject}
                />
              </div>
            </div>
          )}

          {/* Publish dropdown */}
          <div className="bg-muted rounded-md">
            <PublishDropdown
              projectId={projectId}
              onNavigateToSettings={onSettingsClick}
            />
          </div>
        </div>
      )}
      {/* Right: Publish button */}
    </div>
  );
}
